'use client'

import { Box, Tag } from '@chakra-ui/react'
import { keyframes } from '@emotion/react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useColorModeValue } from '@/components/ui/color-mode'
import type { NetworkLogoFragment } from '@/graphql/api/generated'
import { useObjectUrlForFile } from '@/hooks/useObjectUrlForFile'

import { NetworkCardPresentation } from './NetworkCardPresentation'

const previewFloat = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`

export type NetworkCardPreviewProps = {
  name: string
  description: string
  logo?: File | null
  /** Hex color rendered as a thin banner on top of the card. */
  primaryColor?: string
}

/**
 * Step 1 wizard preview: same card shell as {@link NetworkCard} without communities row or location.
 */
export const NetworkCardPreview = ({
  name,
  description,
  logo,
  primaryColor,
}: NetworkCardPreviewProps) => {
  const { t } = useTranslation()
  const logoPreviewSrc = useObjectUrlForFile(logo ?? null)
  const previewShadow = useColorModeValue(
    '0 22px 50px -14px rgba(26, 32, 44, 0.38), 0 8px 20px -8px rgba(26, 32, 44, 0.18)',
    '0 28px 56px -16px rgba(0, 0, 0, 0.65), 0 12px 28px -10px rgba(0, 0, 0, 0.45)'
  )

  const displayName =
    name.trim() || t('networks.wizard.preview.namePlaceholder')

  const logoNetwork = useMemo(
    (): NetworkLogoFragment => ({
      __typename: 'Network',
      name: displayName,
    }),
    [displayName]
  )

  return (
    <Box w="full" maxW="md" mx="auto">
      <Box
        borderRadius="2xl"
        boxShadow={previewShadow}
        animation={`${previewFloat} 5s ease-in-out infinite`}
        willChange="transform"
      >
        <NetworkCardPresentation
          title={displayName}
          description={description.trim() || undefined}
          logoNetwork={logoNetwork}
          logoPreviewSrc={logoPreviewSrc}
          headerTopBanner={
            primaryColor ? <Box h="1.5" bg={primaryColor} /> : undefined
          }
          headerAddon={
            <Tag.Root size="sm" colorPalette="pink">
              <Tag.Label>{t('networks.wizard.preview.badge')}</Tag.Label>
            </Tag.Root>
          }
        />
      </Box>
    </Box>
  )
}
